/*
  Bubble Letters configurator (Speen & Koord)
  - Finish (Glossy/Fluffy) and size (8 cm/12 cm) apply to the whole name.
  - Color is chosen per character.
  - Digits, accents (ë, ô) and other non-space characters are kept and priced.
  - Shopify variant title/option must match the size label. Quantity = character count.
*/
(() => {
  const ROOT_SELECTOR = '[data-bubble-letters]';

  function readJson(root, selector, fallback) {
    const el = root.querySelector(selector);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch (_) {
      return fallback;
    }
  }

  function formatMoney(cents, moneyFormat) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(
          cents,
          moneyFormat || (window.theme && window.theme.moneyFormat) || undefined
        );
      }
    } catch (_) {
      /* fall through */
    }
    const amount = (cents / 100).toFixed(2);
    if (moneyFormat && moneyFormat.includes('{{amount_with_comma_separator}}')) {
      return moneyFormat.replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
    }
    if (moneyFormat && moneyFormat.includes('{{amount}}')) {
      return moneyFormat.replace('{{amount}}', amount);
    }
    return `€${amount.replace('.', ',')}`;
  }

  function graphemes(value) {
    const text = String(value || '');
    if (typeof Intl !== 'undefined' && Intl.Segmenter) {
      return [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text)].map(
        (part) => part.segment
      );
    }
    return Array.from(text);
  }

  function isSpace(character) {
    return /^\s$/u.test(character);
  }

  function upper(character) {
    return isSpace(character) ? character : character.toLocaleUpperCase('nl-BE');
  }

  function limitText(value, maxLetters) {
    const kept = [];
    let count = 0;
    graphemes(value).forEach((character) => {
      if (isSpace(character)) {
        if (kept.length) kept.push(character);
        return;
      }
      if (count >= maxLetters) return;
      kept.push(character);
      count += 1;
    });
    return kept.join('').replace(/\s+$/u, '');
  }

  function init(root) {
    const maxLetters = Number(root.dataset.maxLetters || 15);
    const moneyFormat = root.dataset.moneyFormat || '';
    const cartUrl = root.dataset.cartUrl || '/cart';
    const addUrl = root.dataset.addUrl || '/cart/add.js';
    const strings = readJson(root, '[data-bl-strings]', {});
    const variants = readJson(root, '[data-bl-variants]', []);
    const colors = readJson(root, '[data-bl-colors]', []).filter((c) => c && c.name && c.hex);

    const nameInput = root.querySelector('[data-bl-name]');
    const cards = root.querySelector('[data-bl-cards]');
    const preview = root.querySelector('[data-bl-preview]');
    const priceEl = root.querySelector('[data-bl-price]');
    const countEl = root.querySelector('[data-bl-count]');
    const summaryText = root.querySelector('[data-bl-summary-text]');
    const submitBtn = root.querySelector('[data-bl-submit]');
    const submitLabel = root.querySelector('[data-bl-submit-label]');
    const errorEl = root.querySelector('[data-bl-error]');
    const finishButtons = [...root.querySelectorAll('[data-bl-finish]')];
    const sizeButtons = [...root.querySelectorAll('[data-bl-size]')];

    if (!nameInput || !cards || !preview || !submitBtn) return;

    const defaultLabel = submitLabel ? submitLabel.textContent : '';
    let data = [];
    let finish = '';
    let size = '';

    function variantForSize(label) {
      const want = String(label || '').trim().toLowerCase();
      if (!want) return null;
      return variants.find((variant) =>
        [variant.option1, variant.option2, variant.option3, variant.title]
          .filter(Boolean)
          .some((part) => String(part).trim().toLowerCase() === want)
      );
    }

    function pricing() {
      const variant = variantForSize(size);
      const count = data.length;
      if (!variant || !count) {
        return { variant: variant || null, quantity: count, totalCents: 0 };
      }
      return {
        variant,
        quantity: count,
        totalCents: count * Number(variant.price),
      };
    }

    function setError(text) {
      if (!errorEl) return;
      errorEl.textContent = text || '';
      errorEl.hidden = !text;
    }

    function colorsComplete() {
      return data.length > 0 && data.every((item) => item.color);
    }

    function selectChoice(buttons, attr, value) {
      buttons.forEach((button) => {
        const active = button.getAttribute(attr) === value;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    function rebuild() {
      const limited = limitText(nameInput.value, maxLetters);
      if (limited !== nameInput.value) {
        const pos = nameInput.selectionStart;
        nameInput.value = limited;
        if (typeof pos === 'number') nameInput.setSelectionRange(pos, pos);
      }

      const pieces = graphemes(limited).filter((character) => !isSpace(character));
      const previous = data;
      data = pieces.map((character, index) => ({
        character: upper(character),
        color: previous[index]?.color || null,
      }));

      cards.innerHTML = '';
      data.forEach((item, index) => {
        const card = document.createElement('div');
        card.className = 'bl-letter';

        const head = document.createElement('div');
        head.className = 'bl-letter__head';

        const char = document.createElement('span');
        char.className = 'bl-letter__char';
        char.textContent = item.character;
        char.style.color = item.color ? item.color.hex : '';
        char.setAttribute('aria-hidden', 'true');

        const meta = document.createElement('span');
        meta.className = 'bl-letter__meta';
        meta.textContent = (item.color && item.color.name) || strings.choose || 'Kies een kleur';

        head.appendChild(char);
        head.appendChild(meta);
        card.appendChild(head);

        const grid = document.createElement('div');
        grid.className = 'bl-colors';
        grid.setAttribute('role', 'group');
        grid.setAttribute('aria-label', `${strings.color || 'Kleur voor'} ${item.character}`);
        colors.forEach((color) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          const active = item.color && item.color.name === color.name;
          dot.className = 'bl-color' + (active ? ' is-active' : '');
          dot.style.backgroundColor = color.hex;
          dot.title = color.name;
          dot.setAttribute('aria-label', color.name);
          dot.setAttribute('aria-pressed', active ? 'true' : 'false');
          dot.addEventListener('click', () => {
            data[index].color = color;
            rebuild();
          });
          grid.appendChild(dot);
        });
        card.appendChild(grid);
        cards.appendChild(card);
      });

      updateSummary();
    }

    function updateSummary() {
      preview.innerHTML = '';
      if (!data.length) {
        const placeholder = document.createElement('span');
        placeholder.className = 'bl-preview__placeholder';
        placeholder.textContent = strings.previewPlaceholder || 'Typ hierboven een naam';
        preview.appendChild(placeholder);
      } else {
        let piece = 0;
        graphemes(nameInput.value).forEach((character) => {
          if (isSpace(character)) {
            const gap = document.createElement('span');
            gap.className = 'bl-preview__space';
            gap.setAttribute('aria-hidden', 'true');
            preview.appendChild(gap);
            return;
          }
          const item = data[piece];
          piece += 1;
          const el = document.createElement('span');
          el.className = 'bl-preview__letter';
          el.textContent = upper(character);
          el.style.color = item && item.color ? item.color.hex : '#cfcfcf';
          preview.appendChild(el);
        });
      }

      const { variant, quantity, totalCents } = pricing();
      if (countEl) countEl.textContent = String(data.length);
      if (priceEl) {
        priceEl.textContent = quantity ? formatMoney(totalCents, moneyFormat) : formatMoney(0, moneyFormat);
      }
      if (summaryText) {
        const header = [finish, size].filter(Boolean).join(' · ');
        const lines = data.map((item) =>
          [item.character, '=', item.color && item.color.name].filter(Boolean).join(' ')
        );
        summaryText.textContent = [header, ...lines].filter(Boolean).join('\n');
      }

      const ready = Boolean(finish && size && colorsComplete() && variant && variant.available && quantity > 0);
      submitBtn.disabled = !ready;

      if (data.length && size && !variant) {
        setError(strings.noVariant || 'Maak in Shopify twee varianten: 8 cm en 12 cm.');
      } else if (data.length && size && variant && !variant.available) {
        setError(strings.unavailable || 'Dit formaat is momenteel niet beschikbaar.');
      } else {
        setError('');
      }
    }

    async function addToCart() {
      if (!finish) {
        setError(strings.needFinish || 'Kies glossy of fluffy voor de hele naam.');
        return;
      }
      if (!size) {
        setError(strings.needSize || 'Kies 8 cm of 12 cm voor de hele naam.');
        return;
      }
      if (!colorsComplete()) {
        setError(strings.incomplete || 'Kies een kleur per teken.');
        return;
      }
      const { variant, quantity } = pricing();
      if (!variant) {
        setError(strings.noVariant || 'Maak in Shopify twee varianten: 8 cm en 12 cm.');
        return;
      }

      const properties = {
        Naam: graphemes(nameInput.value).map(upper).join(''),
        Afwerking: finish,
        Formaat: size,
        'Aantal tekens': String(data.length),
        Configuratie: data.map((item) => `${item.character}: ${item.color.name}`).join(' • '),
        _Configurator: JSON.stringify({
          name: nameInput.value,
          finish,
          size,
          characters: data.map((item) => ({
            character: item.character,
            color: item.color.name,
            hex: item.color.hex,
          })),
        }),
      };

      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
      if (submitLabel) submitLabel.textContent = strings.adding || 'Toevoegen…';
      setError('');

      try {
        const res = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variant.id), quantity, properties }],
          }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.description || json.message || 'Er ging iets mis.');
        if (submitLabel) submitLabel.textContent = strings.added || 'Toegevoegd ✓';
        window.location.href = cartUrl;
      } catch (err) {
        setError(err.message || 'Het product kon niet worden toegevoegd.');
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-loading');
        if (submitLabel) submitLabel.textContent = defaultLabel;
      }
    }

    finishButtons.forEach((button) => {
      button.addEventListener('click', () => {
        finish = button.getAttribute('data-bl-finish') || '';
        selectChoice(finishButtons, 'data-bl-finish', finish);
        updateSummary();
      });
    });
    sizeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        size = button.getAttribute('data-bl-size') || '';
        selectChoice(sizeButtons, 'data-bl-size', size);
        updateSummary();
      });
    });

    nameInput.addEventListener('input', rebuild);
    submitBtn.addEventListener('click', addToCart);
    rebuild();
  }

  function initCarousel(root) {
    const track = root.querySelector('[data-bl-track]');
    const slides = [...root.querySelectorAll('[data-bl-slide]')];
    const dots = [...root.querySelectorAll('[data-bl-dot]')];
    const prev = root.querySelector('[data-bl-prev]');
    const next = root.querySelector('[data-bl-next]');
    if (!track || slides.length < 2) return;

    const currentIndex = () => {
      const width = track.clientWidth || 1;
      return Math.round(track.scrollLeft / width);
    };

    const goTo = (index) => {
      const nextIndex = (index + slides.length) % slides.length;
      track.scrollTo({ left: nextIndex * track.clientWidth, behavior: 'smooth' });
    };

    const sync = () => {
      const active = currentIndex();
      dots.forEach((dot, index) => {
        const on = index === active;
        dot.classList.toggle('is-active', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    if (prev) prev.addEventListener('click', () => goTo(currentIndex() - 1));
    if (next) next.addEventListener('click', () => goTo(currentIndex() + 1));
    dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index)));
    track.addEventListener('scroll', () => window.requestAnimationFrame(sync), { passive: true });
    sync();
  }

  document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  document.querySelectorAll('[data-bl-carousel]').forEach(initCarousel);
})();
